import { billingAPI } from '../services/api';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Lazily injects Razorpay's Checkout script once and reuses it on later calls.
let razorpayScriptPromise = null;
const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.onload = resolve;
    script.onerror = () => { razorpayScriptPromise = null; reject(new Error('Failed to load Razorpay checkout script')); };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
};

// Opens Razorpay Checkout in subscription mode for an already-created
// razorpaySubscriptionId (create it server-side first via billingAPI.createSubscription).
//
// `onlyMethod` ('upi' | 'card') limits the window to that one method. Used only when a referral
// Offer was attached: the Offer is tied to one payment method, so showing the others would let the
// customer pay without the discount they were just promised. Anything else leaves the window as is.
// `emandate` is the bank-account autopay method; Razorpay lists it in subscription windows, and no
// referral Offer covers it, so it is switched off too (ignored by Razorpay if it doesn't know the key).
const METHOD_OPTIONS = {
  upi: { upi: true, card: false, netbanking: false, wallet: false, emi: false, paylater: false, emandate: false },
  card: { upi: false, card: true, netbanking: false, wallet: false, emi: false, paylater: false, emandate: false },
};

export const openRazorpaySubscriptionCheckout = async ({ subscriptionId, keyId, planLabel, onlyMethod, onSuccess, onDismiss }) => {
  await loadRazorpayScript();
  const rzp = new window.Razorpay({
    key: keyId,
    subscription_id: subscriptionId,
    name: 'CRM Subscription',
    description: planLabel || 'Subscription renewal',
    handler: () => onSuccess?.(),
    modal: { ondismiss: () => onDismiss?.() },
    theme: { color: '#7c3aed' },
    ...(METHOD_OPTIONS[onlyMethod] ? {
      method: METHOD_OPTIONS[onlyMethod],
      // The `method` switches above did not remove EMandate from the window, so ask for it to be hidden
      // through the display config as well (Razorpay docs: config.display.hide takes method keys).
      config: { display: { hide: [{ method: 'emandate' }] } },
    } : {}),
  });
  rzp.open();
};

// Opens Razorpay Checkout for a one-time Order (auto-renew unticked). `onPaid` receives
// Razorpay's { razorpay_order_id, razorpay_payment_id, razorpay_signature } for server verification.
const openRazorpayOrderCheckout = async ({ orderId, amount, keyId, planLabel, onPaid, onDismiss }) => {
  await loadRazorpayScript();
  const rzp = new window.Razorpay({
    key: keyId,
    order_id: orderId,
    amount,
    currency: 'INR',
    name: 'CRM Subscription',
    description: planLabel || 'Subscription',
    handler: (response) => onPaid?.(response),
    modal: { ondismiss: () => onDismiss?.() },
    theme: { color: '#7c3aed' },
  });
  rzp.open();
};

// One entry point for both payment styles, shared by PlansModal and RenewalRequired:
//   autoRenew true  -> Razorpay Subscription (autopay), activated by the webhook
//   autoRenew false -> one-time Razorpay Order for a single cycle, verified server-side
// onPaymentReceived() fires as soon as the customer has paid; onResult(active) once we know
// whether the plan is live yet (false = still processing, the webhook will finish it).
// `couponCode` only matters for the one-time path (coupons don't apply to autopay); the server
// re-checks it and refuses the order if it isn't valid, so a coupon is never silently dropped.
// `paymentMethod` ('upi' | 'card') only matters for autopay: the summary asks for it because the referral
// Offer belongs to one method; the server attaches that method's Offer and we limit the window to it.
// `referralCode` is an affiliate's code typed on a first paid plan; it works for both payment styles, and the
// server re-checks it (a refused code stops the checkout instead of being dropped).
export const startPlanCheckout = async ({ planId, billingCycle, autoRenew, couponCode, referralCode, paymentMethod, planLabel, onPaymentReceived, onResult, onDismiss }) => {
  if (autoRenew) {
    const { data } = await billingAPI.createSubscription({ planId, billingCycle, ...(paymentMethod ? { paymentMethod } : {}), ...(referralCode ? { referralCode } : {}) });
    await openRazorpaySubscriptionCheckout({
      subscriptionId: data.razorpaySubscriptionId,
      keyId: data.keyId,
      planLabel,
      onlyMethod: data.offerApplied ? data.paymentMethod : undefined,
      onSuccess: async () => {
        onPaymentReceived?.();
        if (data.startMode === 'after_current') {
          // A same/lower plan: autopay is approved now but its first charge is dated for when the current plan
          // ends, so the subscription is 'authenticated' (never 'active' yet) — sync it, don't wait for 'active'.
          try { await billingAPI.syncSubscription(); } catch { /* the webhook still records it */ }
          onResult?.(true, { queuedPlan: { startsAt: data.startsAt } });
          return;
        }
        onResult?.(await pollSubscriptionActive());
      },
      onDismiss,
    });
    return;
  }

  const { data } = await billingAPI.createOrder({ planId, billingCycle, ...(couponCode ? { couponCode } : {}), ...(referralCode ? { referralCode } : {}) });
  await openRazorpayOrderCheckout({
    orderId: data.orderId,
    amount: data.amount,
    keyId: data.keyId,
    planLabel,
    onPaid: async (response) => {
      onPaymentReceived?.();
      try {
        const { data: verified } = await billingAPI.verifyOrder(response);
        // queuedPlan is set when the plan just paid for waits for the current one to end.
        onResult?.(true, { queuedPlan: verified?.queuedPlan || null });
      } catch {
        // Verify call failed (network etc.) — the order.paid webhook will still activate the plan.
        onResult?.(false);
      }
    },
    onDismiss,
  });
};

// The client-side checkout success callback fires before the webhook has
// necessarily landed — the webhook is the source of truth for activation, so
// poll briefly for the subscription to actually flip to 'active' rather than
// assuming success immediately.
export const pollSubscriptionActive = async ({ timeoutMs = 20000, intervalMs = 2000 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const { data } = await billingAPI.getSubscription();
      if (data?.subscription?.status === 'active') return true;
    } catch {
      // ignore transient errors and keep polling until the deadline
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return false;
};
